import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { apiGet, apiPost } from "../lib/api";
import { getRankIcon, isErRank } from "../lib/ranks";
import KabileLeaderPanel from "./KabileLeaderPanel";
import "./KabileDailyBattle.css";

interface DailyBattle {
  _id: string;
  questionText: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
  timeRemaining?: {
    hours: number;
    minutes: number;
    seconds: number;
  };
}

interface Answer {
  _id: string;
  userId: string;
  username: string;
  userTP: number;
  userRank: number;
  answerText: string;
  votes: number;
  hasVoted: boolean;
  isOwn: boolean;
  isFounder?: boolean;
  isCurrentLeader?: boolean;
  createdAt?: string;
}

interface MyKabile {
  id: string;
  name: string;
  flagUrl?: string;
  leaderId: string;
  isLeader: boolean;
}

export default function KabileDailyBattle() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [battle, setBattle] = useState<DailyBattle | null>(null);
  const [myKabile, setMyKabile] = useState<MyKabile | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [myAnswer, setMyAnswer] = useState<Answer | null>(null);
  
  // Form states
  const [showAnswerForm, setShowAnswerForm] = useState(false);
  const [answerText, setAnswerText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // Timer
  const [timeLeft, setTimeLeft] = useState<string>("");

  useEffect(() => {
    loadBattleData();
    
    // Update timer every second
    const interval = setInterval(() => {
      updateTimer();
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const loadBattleData = async () => {
    try {
      setLoading(true);
      
      // Get current battle and user's kabile
      const [battleRes, kabileRes] = await Promise.all([
        apiGet<{ ok: true; battle: DailyBattle | null }>("/api/kabile/daily-battle"),
        apiGet<MyKabile>("/api/kabile/my")
      ]);
      
      if (!kabileRes) {
        // User not in a kabile
        navigate("/savkabile");
        return;
      }
      
      setMyKabile(kabileRes);
      setBattle(battleRes.battle);
      
      if (battleRes.battle) {
        // Load answers for this kabile
        await loadAnswers();
      }
    } catch (error: any) {
      console.error("Failed to load battle data:", error);
      alert(error.message || "Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const loadAnswers = async () => {
    try {
      const res = await apiGet<{ ok: true; answers: Answer[]; myAnswer: Answer | null }>(
        "/api/kabile/daily-battle/answers"
      );
      
      setAnswers(res.answers);
      setMyAnswer(res.myAnswer);
      
      if (res.myAnswer) {
        setAnswerText(res.myAnswer.answerText);
      }
    } catch (error: any) {
      console.error("Failed to load answers:", error);
    }
  };

  const updateTimer = () => {
    if (!battle) return;
    
    const now = new Date().getTime();
    const end = new Date(battle.endTime).getTime();
    const distance = end - now;
    
    if (distance < 0) {
      setTimeLeft("Süre doldu");
      return;
    }
    
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    
    setTimeLeft(`${hours}s ${minutes}d ${seconds}sn`);
  };

  const handleSubmitAnswer = async () => {
    if (!answerText.trim()) {
      alert("Lütfen cevabınızı yazın");
      return;
    }
    
    if (answerText.length < 20) {
      alert("Cevabınız en az 20 karakter olmalıdır");
      return;
    }
    
    if (answerText.length > 1000) {
      alert("Cevabınız en fazla 1000 karakter olabilir");
      return;
    }
    
    try {
      setSubmitting(true);
      
      await apiPost("/api/kabile/daily-battle/answer", {
        answerText: answerText.trim()
      });
      
      await loadAnswers();
      setShowAnswerForm(false);
      alert("Cevabınız başarıyla gönderildi");
    } catch (error: any) {
      console.error("Failed to submit answer:", error);
      alert(error.message || "Cevap gönderilirken hata oluştu");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateAnswer = async () => {
    if (!myAnswer) return;
    
    if (!answerText.trim()) {
      alert("Lütfen cevabınızı yazın");
      return;
    }
    
    if (answerText.length < 20) {
      alert("Cevabınız en az 20 karakter olmalıdır");
      return;
    }
    
    try {
      setSubmitting(true);
      
      await apiPost("/api/kabile/daily-battle/answer/update", {
        answerText: answerText.trim()
      });
      
      await loadAnswers();
      alert("Cevabınız güncellendi");
    } catch (error: any) {
      console.error("Failed to update answer:", error);
      alert(error.message || "Cevap güncellenirken hata oluştu");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAnswer = async () => {
    if (!confirm("Cevabınızı silmek istediğinizden emin misiniz?")) {
      return;
    }
    
    try {
      await apiPost("/api/kabile/daily-battle/answer/delete", {});
      
      setMyAnswer(null);
      setAnswerText("");
      await loadAnswers();
      alert("Cevabınız silindi");
    } catch (error: any) {
      console.error("Failed to delete answer:", error);
      alert(error.message || "Cevap silinirken hata oluştu");
    }
  };

  const handleVote = async (answerId: string) => {
    try {
      await apiPost(`/api/kabile/daily-battle/answer/${answerId}/vote`, {});
      await loadAnswers();
    } catch (error: any) {
      console.error("Failed to vote:", error);
      alert(error.message || "Oy kullanılamadı");
    }
  };

  const getVoteWeight = (tp: number): number => {
    if (tp >= 1000) return 4;
    if (tp >= 500) return 3;
    if (tp >= 100) return 2;
    return 1;
  };

  if (loading) {
    return (
      <div className="daily-battle-container">
        <div className="battle-loading">
          <div className="loading-spinner"></div>
          <p>Günlük Battle yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!battle) {
    return (
      <div className="daily-battle-container">
        <div className="daily-battle-content">
          <button onClick={() => navigate("/savkabile")} className="back-button">
            ← Geri Dön
          </button>
          
          <div className="no-battle-card">
            <div className="no-battle-icon">⏰</div>
            <h2>Günün Sorusu Henüz Yayınlanmadı</h2>
            <p>Her gün saat 21:00'de yeni bir soru yayınlanacak.</p>
            <p>Sabırsızlanma, yakında başlayacak! 🔥</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="daily-battle-container">
      <div className="daily-battle-content">
        {/* Header */}
        <header className="battle-header">
          <button onClick={() => navigate("/savkabile")} className="back-button">
            ← Geri Dön
          </button>
          
          <div className="kabile-info">
            {myKabile?.flagUrl && (
              <img src={myKabile.flagUrl} alt={myKabile.name} className="kabile-flag-small" />
            )}
            <span className="kabile-name">{myKabile?.name}</span>
          </div>
        </header>

        {/* Question Card */}
        <div className="question-card">
          <div className="question-badge">
            <span className="badge-icon">📌</span>
            <span className="badge-text">Bugünün Sorusu</span>
          </div>
          
          <h2 className="question-text">{battle.questionText}</h2>
          
          <div className="question-meta">
            <div className="time-remaining">
              <span className="meta-icon">⏰</span>
              <span className="meta-text">Kalan Süre: <strong>{timeLeft}</strong></span>
            </div>
            <div className="answer-count">
              <span className="meta-icon">💬</span>
              <span className="meta-text">{answers.length} Cevap</span>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="info-card">
          <h3>ℹ️ Nasıl Çalışır?</h3>
          <ul>
            <li>Kabile üyeleri soruya cevap yazar</li>
            <li>Kabile üyeleri ve lider cevaplara like atar</li>
            <li>En çok like alan cevaplar lidere gösterilir</li>
            <li>Lider istediğini global arenaya paylaşır</li>
            <li>Global arenada alınan likeler Kabile Puanı (KP) kazandırır</li>
          </ul>
        </div>

        {/* Leader Panel - TOP 3 Selection */}
        {myKabile?.isLeader && <KabileLeaderPanel />}

        {/* User's Answer Section */}
        <div className="my-answer-section">
          <h3>✍️ Senin Cevabın</h3>
          
          {myAnswer ? (
            <div className="my-answer-card">
              <div className="answer-content">
                <p className="answer-text">{myAnswer.answerText}</p>
                <div className="answer-stats">
                  <span className="stat-item">
                    <span className="stat-icon">⚔️</span>
                    <span className="stat-value">{myAnswer.userTP} TP</span>
                  </span>
                  <span className="stat-item">
                    {isErRank(Number(myAnswer.userRank)) ? (
                      <span style={{ fontSize: 20 }}>🟢</span>
                    ) : (
                      <img 
                        src={getRankIcon(Number(myAnswer.userRank))} 
                        alt="rank" 
                        className="stat-rank-icon"
                      />
                    )}
                  </span>
                  <span className="stat-item">
                    <span className="stat-icon">👍</span>
                    <span className="stat-value">{myAnswer.votes} Oy</span>
                  </span>
                  {myAnswer.isFounder && (
                    <span className="stat-item founder-badge-inline">
                      👑 Kurucu Lider
                    </span>
                  )}
                  {myAnswer.isCurrentLeader && (
                    <span className="stat-item leader-badge-inline">
                      ⚔️ Lider
                    </span>
                  )}
                </div>
              </div>
              
              <div className="answer-actions">
                {(() => {
                  if (!myAnswer.createdAt) return null;
                  const createdAt = new Date(myAnswer.createdAt);
                  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
                  const canEdit = createdAt >= fiveMinutesAgo;
                  
                  return canEdit ? (
                    <button 
                      onClick={() => setShowAnswerForm(!showAnswerForm)} 
                      className="edit-answer-btn"
                    >
                      ✏️ Düzenle
                    </button>
                  ) : null;
                })()}
                <button onClick={handleDeleteAnswer} className="delete-answer-btn">
                  🗑️ Sil
                </button>
              </div>
              
              {showAnswerForm && (
                <div className="edit-form">
                  <textarea
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    placeholder="Cevabını düzenle..."
                    rows={6}
                    className="answer-textarea"
                    maxLength={1000}
                  />
                  <div className="char-count">{answerText.length} / 1000</div>
                  <div className="form-actions">
                    <button 
                      onClick={handleUpdateAnswer} 
                      disabled={submitting}
                      className="submit-btn"
                    >
                      {submitting ? "Kaydediliyor..." : "💾 Kaydet"}
                    </button>
                    <button 
                      onClick={() => {
                        setShowAnswerForm(false);
                        setAnswerText(myAnswer.answerText);
                      }}
                      className="cancel-btn"
                    >
                      ❌ İptal
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {!showAnswerForm ? (
                <button 
                  onClick={() => setShowAnswerForm(true)} 
                  className="write-answer-btn"
                >
                  ➕ Cevap Yaz
                </button>
              ) : (
                <div className="answer-form">
                  <textarea
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    placeholder="Soruya cevabını yaz... (Min 20, max 1000 karakter)"
                    rows={6}
                    className="answer-textarea"
                    maxLength={1000}
                  />
                  <div className="char-count">{answerText.length} / 1000</div>
                  <div className="form-actions">
                    <button 
                      onClick={handleSubmitAnswer} 
                      disabled={submitting}
                      className="submit-btn"
                    >
                      {submitting ? "Gönderiliyor..." : "📤 Gönder"}
                    </button>
                    <button 
                      onClick={() => {
                        setShowAnswerForm(false);
                        setAnswerText("");
                      }}
                      className="cancel-btn"
                    >
                      ❌ İptal
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* All Answers Section */}
        <div className="answers-section">
          <h3>💬 Kabile Cevapları ({answers.length})</h3>
          
          {answers.length === 0 ? (
            <div className="no-answers">
              <p>Henüz kimse cevap vermedi. İlk sen ol! 🚀</p>
            </div>
          ) : (
            <div className="answers-list">
              {answers.map((answer, index) => (
                <div 
                  key={answer._id} 
                  className={`answer-card ${answer.isOwn ? 'own-answer' : ''}`}
                >
                  <div className="answer-header">
                    <div className="author-info">
                      <span className="author-name">{answer.username}</span>
                      {isErRank(Number(answer.userRank)) ? (
                        <span style={{ fontSize: 20 }}>🟢</span>
                      ) : (
                        <img 
                          src={getRankIcon(Number(answer.userRank))} 
                          alt="rank" 
                          className="author-rank-icon"
                        />
                      )}
                      <span className="author-tp">{answer.userTP} TP</span>
                      {answer.isFounder && (
                        <span className="leader-badge founder-badge">👑 Kurucu Lider</span>
                      )}
                      {answer.isCurrentLeader && (
                        <span className="leader-badge current-leader-badge">⚔️ Lider</span>
                      )}
                    </div>
                    
                    {!answer.isOwn && (
                      <button 
                        onClick={() => handleVote(answer._id)}
                        disabled={answer.hasVoted}
                        className={`vote-btn ${answer.hasVoted ? 'voted' : ''}`}
                      >
                        {answer.hasVoted ? '✓ Oy Verildi' : '👍 Oy Ver'}
                      </button>
                    )}
                  </div>
                  
                  <p className="answer-text">{answer.answerText}</p>
                  
                  <div className="answer-footer">
                    <div className="vote-info">
                      <span className="vote-count">{answer.votes} Ağırlıklı Oy</span>
                      {!answer.isOwn && user && (
                        <span className="vote-weight">
                          (Senin oy gücün: {getVoteWeight(user.savwarTP || 0)})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
